import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Download, CalendarCheck } from 'lucide-react';
import { usePageTitle } from '../components/AppShell';
import { useLang } from '../lib/lang';
import { type AuthUser } from '../lib/auth';
import { T } from '../lib/tokens';

interface Props { user: AuthUser }

type AttStatus = 'HADIR' | 'LEWAT' | 'TIDAK_HADIR' | 'CUTI' | 'WEEKEND' | 'FUTURE' | 'HOLIDAY';

interface DayRecord { date: number; status: AttStatus; note?: string }

// Realistic June 2025 attendance for child
const JUNE_2025: DayRecord[] = [
  { date:2,  status:'HADIR'     },
  { date:3,  status:'HADIR'     },
  { date:4,  status:'HADIR'     },
  { date:5,  status:'LEWAT',    note:'Trafik teruk' },
  { date:6,  status:'HADIR'     },
  { date:9,  status:'HADIR'     },
  { date:10, status:'TIDAK_HADIR', note:'Sakit — MC dikemukakan' },
  { date:11, status:'HADIR'     },
  { date:12, status:'HADIR'     },
  { date:13, status:'HADIR'     },
  { date:16, status:'HADIR'     },
  { date:17, status:'HADIR'     },
  { date:18, status:'HADIR'     },
  { date:19, status:'HADIR'     },
  { date:20, status:'HADIR'     },
  { date:23, status:'HADIR'     },
  { date:24, status:'HADIR'     },
  { date:25, status:'HADIR'     },
  { date:26, status:'HADIR'     },
  { date:27, status:'FUTURE'    },
  { date:30, status:'FUTURE'    },
];

const WEEKENDS_JUNE = [1, 7, 8, 14, 15, 21, 22, 28, 29];
const MONTH_START_DOW = 0; // June 1 2025 = Sunday

const STATUS_STYLE: Record<AttStatus, { bg: string; color: string; dot: string; label: string; labelEn: string }> = {
  HADIR:       { bg: T.color.successLight, color: T.color.success, dot: T.color.success,  label: 'Hadir',        labelEn: 'Present'  },
  LEWAT:       { bg: T.color.warningLight, color: T.color.warning, dot: T.color.warning,  label: 'Lewat',        labelEn: 'Late'     },
  TIDAK_HADIR: { bg: T.color.dangerLight,  color: T.color.danger,  dot: T.color.danger,   label: 'Tidak Hadir',  labelEn: 'Absent'   },
  CUTI:        { bg: '#EFF6FF',            color: '#1D4ED8',        dot: '#1D4ED8',        label: 'Cuti',         labelEn: 'Leave'    },
  WEEKEND:     { bg: '#F9FAFB',           color: '#9CA3AF',        dot: '#D1D5DB',        label: 'Hujung Minggu',labelEn: 'Weekend'  },
  FUTURE:      { bg: '#F9FAFB',           color: '#D1D5DB',        dot: '#E5E7EB',        label: '—',            labelEn: '—'        },
  HOLIDAY:     { bg: '#F5F3FF',           color: '#7C3AED',        dot: '#7C3AED',        label: 'Cuti Umum',    labelEn: 'Holiday'  },
};

const DAYS_MS = ['Ahd','Isn','Sel','Rab','Kha','Jum','Sab'];
const DAYS_EN = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

export function ParentKehadiranPage({ user }: Props) {
  const { lang } = useLang();
  const E = lang === 'en';
  usePageTitle(E ? 'Attendance' : 'Kehadiran');

  const [selectedChild] = useState('Ahmad Zulfikri');
  const children = ['Ahmad Zulfikri', 'Nurul Izzati'];

  // Build full month grid
  const totalDays = 30; // June
  const days = DAYS_MS;

  const recordMap: Record<number, DayRecord> = {};
  for (const r of JUNE_2025) recordMap[r.date] = r;

  const hadir      = JUNE_2025.filter(r => r.status === 'HADIR').length;
  const lewat      = JUNE_2025.filter(r => r.status === 'LEWAT').length;
  const tidakHadir = JUNE_2025.filter(r => r.status === 'TIDAK_HADIR').length;
  const schoolDays = hadir + lewat + tidakHadir;
  const rate       = schoolDays > 0 ? Math.round(((hadir + lewat) / schoolDays) * 100) : 0;

  const detailRows = JUNE_2025
    .filter(r => r.status !== 'FUTURE' && r.status !== 'WEEKEND')
    .sort((a, b) => a.date - b.date);

  return (
    <div style={{ maxWidth: '960px' }}>
      {/* ── Page header ── */}
      <div className="page-header">
        <div>
          <h1 style={{ fontFamily: T.font.serif, fontSize: '24px', fontWeight: 700, color: T.color.textPrimary, marginBottom: '4px' }}>
            {E ? 'Attendance' : 'Kehadiran'}
          </h1>
          <p style={{ fontSize: '13px', color: T.color.textSecondary }}>
            {E ? 'Monthly attendance calendar & summary' : 'Kalendar kehadiran & ringkasan bulanan'}
          </p>
        </div>
        <button className="btn-secondary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Download size={14} />
          {E ? 'Download Report' : 'Muat Turun Laporan'}
        </button>
      </div>

      {/* ── Child selector ── */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
        {children.map(c => (
          <button
            key={c}
            className={`pill-filter${selectedChild === c ? ' active' : ''}`}
            onClick={() => {}}
          >
            {c}
          </button>
        ))}
      </div>

      {/* ── Month header ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
        <button className="btn-icon"><ChevronLeft size={18} /></button>
        <h2 style={{ fontFamily: T.font.serif, fontSize: '18px', fontWeight: 600, color: T.color.textPrimary }}>
          {E ? 'June 2025' : 'Jun 2025'}
        </h2>
        <button className="btn-icon"><ChevronRight size={18} /></button>
      </div>

      {/* ── Calendar grid ── */}
      <div style={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '10px', overflow: 'hidden', marginBottom: '20px' }}>
        {/* Day headers */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid #E5E7EB' }}>
          {(E ? DAYS_EN : DAYS_MS).map(d => (
            <div key={d} style={{ padding: '10px 4px', textAlign: 'center', fontSize: '11px', fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              {d}
            </div>
          ))}
        </div>

        {/* Day cells */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
          {/* Leading empty cells */}
          {Array.from({ length: MONTH_START_DOW }).map((_, i) => (
            <div key={`empty-${i}`} style={{ minHeight: '52px', borderRight: '1px solid #F3F4F6', borderBottom: '1px solid #F3F4F6' }} />
          ))}

          {Array.from({ length: totalDays }, (_, i) => {
            const date = i + 1;
            const col  = (MONTH_START_DOW + i) % 7;
            const isWeekend = WEEKENDS_JUNE.includes(date);
            const rec  = recordMap[date];
            const status: AttStatus = rec ? rec.status : (isWeekend ? 'WEEKEND' : date > 26 ? 'FUTURE' : 'HADIR');
            const st   = STATUS_STYLE[status];
            const isToday = date === 2; // June 2 for demo

            return (
              <div
                key={date}
                title={rec?.note}
                style={{
                  minHeight: '52px', padding: '6px 8px',
                  borderRight: col < 6 ? '1px solid #F3F4F6' : 'none',
                  borderBottom: '1px solid #F3F4F6',
                  background: isWeekend ? '#FAFAFA' : status === 'FUTURE' ? '#FAFAFA' : st.bg + '55',
                  position: 'relative',
                  cursor: rec?.note ? 'help' : 'default',
                }}
              >
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: '24px', height: '24px',
                  fontSize: '12px', fontWeight: 600,
                  color: isToday ? '#111827' : isWeekend ? '#9CA3AF' : status === 'FUTURE' ? '#D1D5DB' : '#374151',
                  borderRadius: '50%',
                  border: isToday ? '2px solid #F7D000' : 'none',
                }}>
                  {date}
                </span>
                {!isWeekend && status !== 'FUTURE' && (
                  <div style={{
                    width: '6px', height: '6px',
                    borderRadius: '50%',
                    background: st.dot,
                    position: 'absolute', bottom: '8px', left: '50%',
                    transform: 'translateX(-50%)',
                  }} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Monthly summary strip ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '28px' }}>
        {[
          { label: E ? 'Days Present' : 'Hari Hadir',        val: hadir,      color: T.color.success, bg: T.color.successLight },
          { label: E ? 'Days Absent' : 'Hari Tidak Hadir',   val: tidakHadir, color: T.color.danger,  bg: T.color.dangerLight  },
          { label: E ? 'Days Late' : 'Hari Lewat',           val: lewat,      color: T.color.warning, bg: T.color.warningLight },
          { label: E ? 'Attendance Rate' : 'Kadar Kehadiran', val: `${rate}%`,color: rate >= 80 ? T.color.success : T.color.danger, bg: rate >= 80 ? T.color.successLight : T.color.dangerLight },
        ].map(m => (
          <div key={m.label} style={{ background: m.bg, border: `1px solid ${m.color}30`, borderRadius: '10px', padding: '16px 20px' }}>
            <div style={{ fontSize: '24px', fontFamily: T.font.serif, fontWeight: 700, color: m.color }}>{m.val}</div>
            <div style={{ fontSize: '12px', color: '#374151', marginTop: '4px' }}>{m.label}</div>
          </div>
        ))}
      </div>

      {/* ── Detail table ── */}
      <div style={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '10px', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 700, color: T.color.textPrimary }}>
            {E ? 'Attendance Detail' : 'Rekod Kehadiran Terperinci'}
          </h3>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {[E?'Date':'Tarikh', E?'Day':'Hari', E?'Status':'Status', E?'Notes':'Catatan'].map(h => (
                <th key={h} className="tbl-header-cell">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {detailRows.map(r => {
              const st = STATUS_STYLE[r.status];
              const dayOfWeek = (MONTH_START_DOW + r.date - 1) % 7;
              return (
                <tr key={r.date} className="tbl-row">
                  <td className="tbl-cell" style={{ fontWeight: 500 }}>
                    {r.date} {E ? 'Jun 2025' : 'Jun 2025'}
                  </td>
                  <td className="tbl-cell" style={{ color: T.color.textSecondary }}>
                    {E ? DAYS_EN[dayOfWeek] : DAYS_MS[dayOfWeek]}
                  </td>
                  <td className="tbl-cell">
                    <span className="badge" style={{ background: st.bg, color: st.color }}>
                      {E ? st.labelEn : st.label}
                    </span>
                  </td>
                  <td className="tbl-cell" style={{ color: T.color.textSecondary, fontSize: '12px' }}>
                    {r.note ?? '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
